import { Flame, Compass, HandCoins, Send } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAppStoreOptional } from '@/store/AppStoreProvider';
import { useSession } from '@/store/session';
import { usePreferences } from '@/store/preferences';
import BasketballIcon from '@/components/icons/BasketballIcon';
import { useState, useCallback, useRef } from 'react';

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUserId } = useSession();
  const { t } = usePreferences();
  const [tappedPath, setTappedPath] = useState<string | null>(null);
  const tapTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const NAV_ITEMS = [
    { icon: Flame, label: t.nav_feed, path: '/' },
    { icon: Compass, label: t.nav_explore, path: '/explore' },
    { icon: BasketballIcon, label: t.nav_create, path: '/create-post', accent: true },
    { icon: HandCoins, label: t.nav_offers, path: '/offers' },
    { icon: Send, label: t.nav_messages, path: '/messages', badge: true },
  ] as const;

  // Optional: the nav can render outside the app store (onboarding chrome).
  const store = useAppStoreOptional();
  let unreadCount = 0;
  if (store) {
    const { message } = store;
    const threads = message.threads.filter((th) => th.participants.includes(message.currentUserId));
    unreadCount = threads.filter((th) => {
      const lastMsg = th.messages.length ? th.messages[th.messages.length - 1] : null;
      return lastMsg && lastMsg.senderId !== message.currentUserId;
    }).length;
  }

  const triggerTap = useCallback((path: string) => {
    if (tapTimeoutRef.current) clearTimeout(tapTimeoutRef.current);
    setTappedPath(path);
    // Haptic
    if (navigator.vibrate) navigator.vibrate(8);
    tapTimeoutRef.current = setTimeout(() => setTappedPath(null), 180);
  }, []);

  const isAlreadyOnPath = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (path: string) => {
    triggerTap(path);

    if (isAlreadyOnPath(path)) {
      // Dispatch a per-tab refresh event
      const eventMap: Record<string, string> = {
        '/': 'feed-refresh',
        '/explore': 'explore-refresh',
        '/offers': 'offers-refresh',
        '/messages': 'messages-refresh',
      };
      const eventName = eventMap[path];
      if (eventName) {
        window.dispatchEvent(new CustomEvent(eventName));
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    navigate(path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/50 bg-background/95 backdrop-blur-md">
      <div className="mx-auto flex max-w-lg items-center justify-around py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {NAV_ITEMS.map(({ icon: Icon, label, path, ...rest }) => {
          const isAccent = 'accent' in rest && rest.accent;
          const hasBadge = 'badge' in rest && rest.badge;
          const active = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
          const isTapped = tappedPath === path;

          return (
            <button
              key={path}
              onClick={() => handleNavClick(path)}
              className={cn(
                'relative flex flex-col items-center gap-0.5 rounded-xl px-4 py-2 transition-all duration-150 ease-out',
                active
                  ? 'text-primary'
                  : 'text-muted-foreground/70 hover:text-muted-foreground'
              )}
              style={{
                transform: isTapped
                  ? isAccent
                    ? 'scale(1.18)'
                    : 'scale(0.88)'
                  : 'scale(1)',
                transition: 'transform 150ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              }}
              aria-label={label}
            >
              {/* Glow ring for Create on tap */}
              {isAccent && isTapped && (
                <span
                  className="absolute inset-0 rounded-xl bg-primary/10 animate-[ping_400ms_ease-out_1]"
                  style={{ pointerEvents: 'none' }}
                />
              )}

              <Icon
                className={cn(
                  'h-[22px] w-[22px] transition-all duration-150',
                  active && 'drop-shadow-[0_0_6px_hsl(var(--primary)/0.35)]',
                  isAccent && isTapped && 'drop-shadow-[0_0_10px_hsl(var(--primary)/0.6)]'
                )}
                strokeWidth={active || isAccent ? 2.2 : 1.6}
              />

              {/* Unread badge with pulse */}
              {hasBadge && unreadCount > 0 && (
                <span className="absolute -right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground shadow-lg shadow-primary/30">
                  {unreadCount > 99 ? '99+' : unreadCount}
                  <span className="absolute inset-0 rounded-full bg-primary animate-[ping_1.5s_cubic-bezier(0,0,0.2,1)_infinite] opacity-30" />
                </span>
              )}

              <span className={cn(
                'text-[10px] transition-all duration-150',
                active ? 'font-semibold' : 'font-medium'
              )}>{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
